import { useRef, useState } from 'react';
import { Avatar, Box, Card, IconButton, Typography } from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { useRouteLoaderData } from 'react-router-dom';
import { toast } from 'react-toastify';

import ToastMessage from '@/components/toasts/ToastMessage';
import api from '@/services/ApiService';
import files from '@/services/FileService';
import tenant from '@/services/TenantService';
import updateFaviconLink from '@/services/utils/updateFaviconLink';

import useAppStore from '@/store/app';

export default function FavIconForm() {
  const { client, config } = useRouteLoaderData('auth');
  const { deployment } = client;

  const { favIconFileId, setFavIconFileId } = useAppStore();

  const [favIconFile, setFavIconFile] = useState(null);

  const favIconFileInputRef = useRef(null);

  const handleFaviconEditClick = () => {
    if (favIconFileInputRef.current) {
      favIconFileInputRef.current.click();
    }
  };

  const handleFaviconFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await uploadFavicon(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleFaviconDrop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      await uploadFavicon(file);
    }
  };

  const uploadFavicon = async (file) => {
    if (!file) {
      return;
    }
    try {
      const { data: fileObj } = await api.post('/file', {
        filename: `favicon/${deployment._id}.svg`,
        size: 1000000,
        public: true,
      });

      if (fileObj.acknowledged) {
        const newFileId = fileObj.insertedId;

        await tenant.put(
          `/deployment/${deployment._id}`,
          JSON.stringify({ config: { ...config, favIconFileId: newFileId } }),
        );

        await files.post(`/${newFileId}`, { file });

        setFavIconFile(file);
        setFavIconFileId(newFileId);

        updateFaviconLink(newFileId);

        toast.success(
          <ToastMessage title="Erfolg" subtitle="FavIcon aktualisiert" />,
        );
      }
    } catch (error) {
      toast.error(
        <ToastMessage
          title="Fehler"
          subtitle={error?.response?.data?.message || 'Upload fehlgeschlagen'}
        />,
      );
    }
  };

  const deleteFavicon = async () => {
    try {
      if (favIconFileId) {
        await files.post(`/delete/${favIconFileId}`);
      }

      await tenant.put(
        `/deployment/${deployment._id}`,
        JSON.stringify({ config: { ...config, favIconFileId: null } }),
      );

      setFavIconFile(null);
      setFavIconFileId(null);

      updateFaviconLink(null);

      toast.success(
        <ToastMessage title="Erfolg" subtitle="FavIcon entfernt" />,
      );
    } catch (error) {
      toast.error(
        <ToastMessage
          title="Fehler"
          subtitle={error?.response?.data?.message || 'Löschen fehlgeschlagen'}
        />,
      );
    }
  };

  return (
    <Card sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        FavIcon
      </Typography>

      <Box>
        <Box
          sx={{
            position: 'relative',
            height: '100px',
            width: '100px',
            '&:hover .avatar-actions': {
              opacity: 1,
            },
          }}
          onDrop={handleFaviconDrop}
          onDragOver={handleDragOver}
        >
          <Avatar
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: '0%',
            }}
            src={
              favIconFile
                ? URL.createObjectURL(favIconFile)
                : favIconFileId
                  ? `${import.meta.env.VITE_FILE_URL}/${favIconFileId}`
                  : ''
            }
            imgProps={{ style: { objectFit: 'contain' } }}
          />

          <Box
            className="avatar-actions"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'row',
              justifyContent:
                favIconFileId || favIconFile ? 'space-around' : 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              opacity: 0,
              transition: 'opacity 0.3s',
            }}
          >
            {favIconFileId || favIconFile ? (
              <>
                <IconButton
                  onClick={handleFaviconEditClick}
                  sx={{ color: 'white' }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton onClick={deleteFavicon} sx={{ color: 'white' }}>
                  <DeleteIcon />
                </IconButton>
              </>
            ) : (
              <IconButton
                onClick={handleFaviconEditClick}
                sx={{ color: 'white' }}
              >
                <AddIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        <input
          ref={favIconFileInputRef}
          type="file"
          accept="image/svg+xml"
          style={{ display: 'none' }}
          onChange={handleFaviconFileChange}
        />
      </Box>
    </Card>
  );
}
